import { SkyBadge, SkyText, SkyTooltip } from "@skypointcloud/skypoint-ui";
import {
  credentialAttention,
  credentialAttentionLabel,
  type CredentialAttention,
} from "@shared/credentials/expiry";
import { SQL_SIGN_IN_CHECK_SENTENCE, isSqlSignInCheck } from "@shared/connectors/sqlSignInRefusal";
import { dbtRepoCheckSentence } from "@shared/credentials/dbtRepoCheck";
import { warehouseCheckSentence } from "@shared/credentials/warehouseCheck";
import type { CredentialExpiryView, CredentialTargetView } from "@shared/types/credentials";
import { CONNECTION_CHECK_SENTENCES, LOAD_CAUSE_SENTENCES } from "@shared/types/notifications";
import { formatCalendarDay, formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * What the daily credential check found, in words (2026-09-28 cred-check design, "UI"). Status by
 * colour AND a label, never colour alone (Skypoint design rules). The words never carry a value,
 * a secret name or the hub's message: only the closed cause, times and dates.
 */

/** The attention a target needs now, or null. */
export function attentionOf(target: CredentialTargetView | null): CredentialAttention | null {
  if (!target) return null;
  return credentialAttention(
    {
      status: target.status,
      expiries: target.expiries.map((e) => ({ expiresAt: new Date(e.expiresAt) })),
    },
    new Date(),
  );
}

/**
 * The header badge beside a connection's status, only when something needs attention:
 * "Credentials refused" (danger), "Credentials expire in 6 days" (warning), "Credentials expired"
 * (danger). The connection's own Status word does not change: a refused credential is not a
 * failed import until an import fails.
 */
export function CredentialAttentionBadge({
  target,
  size = "medium",
  testId,
}: {
  target: CredentialTargetView | null;
  size?: "medium" | "large";
  testId?: string;
}) {
  const attention = attentionOf(target);
  if (!attention) return null;
  const label = credentialAttentionLabel(attention, target ?? undefined);
  return (
    <SkyBadge
      appearance="tint"
      color={attention.kind === "expiring" ? "warning" : "danger"}
      size={size}
      testId={testId}
      aria-label={label}
    >
      {label}
    </SkyBadge>
  );
}

/**
 * The Connections list's small badge: the word "Credentials", its colour by urgency, and the
 * reason in the tooltip and the accessible name. Nothing when nothing needs attention.
 */
export function CredentialListBadge({ target }: { target: CredentialTargetView | null }) {
  const attention = attentionOf(target);
  if (!attention || !target) return null;
  const label = credentialAttentionLabel(attention, target);
  return (
    <SkyTooltip content={label}>
      <SkyBadge
        appearance="tint"
        color={attention.kind === "expiring" ? "warning" : "danger"}
        testId={`connection-credentials-${target.targetId}`}
        aria-label={label}
      >
        Credentials
      </SkyBadge>
    </SkyTooltip>
  );
}

/**
 * "refused." or "refused. <the cause>.", each sentence ending with a full stop. #1097: a SQL
 * connection's `auth` refusal names the database name and port too, as its validation card does.
 */
function causeWords(target: CredentialTargetView): string {
  if (!target.causeCode) return "refused.";
  let sentence: string;
  // #1674: the dbt repository's own words, never the load's table or stage.
  const dbtRepo = dbtRepoCheckSentence(target);
  // #1730: a warehouse key or database the check could not use, never the load's table or stage.
  const warehouse = warehouseCheckSentence(target);
  if (dbtRepo !== null) sentence = dbtRepo;
  else if (warehouse !== null) sentence = warehouse;
  else if (isSqlSignInCheck(target)) sentence = SQL_SIGN_IN_CHECK_SENTENCE;
  else if (target.targetType === "dataflow")
    sentence = CONNECTION_CHECK_SENTENCES[target.causeCode];
  else sentence = LOAD_CAUSE_SENTENCES[target.causeCode];
  return /[.!?]$/.test(sentence) ? `refused. ${sentence}` : `refused. ${sentence}.`;
}

/**
 * "Checked daily. Last check <time>: accepted." and its other forms. Each time is in the reader's
 * display zone, named (`zone`: `useDisplayTz()`).
 */
export function checkSentence(
  target: CredentialTargetView | null,
  checksEnabled: boolean,
  zone: string,
): string {
  if (!checksEnabled) return "Daily credential checks are off for this deployment.";
  if (!target || target.status === "not_checked" || !target.checkedAt) {
    return "Checked daily. Not checked yet.";
  }
  const at = formatInstant(target.checkedAt, zone, "short", { named: true });
  switch (target.status) {
    case "valid":
      return `Checked daily. Last check ${at}: accepted.`;
    case "rejected": {
      // Owner answer (b2) keeps a refusal standing through a day the source did not answer: the
      // refusal is the last ANSWER (`reachedAt`), not the last check (`checkedAt`).
      const answeredAt = target.reachedAt;
      if (answeredAt && Date.parse(answeredAt) < Date.parse(target.checkedAt)) {
        return `Checked daily. Last answer ${formatInstant(answeredAt, zone, "short", { named: true })}: ${causeWords(target)} The source did not answer at ${at}.`;
      }
      return `Checked daily. Last check ${at}: ${causeWords(target)}`;
    }
    case "unreachable":
      // Owner answer (b2): not a credential failure. The source may be off at check time; the
      // check tries again thirty minutes before the next scheduled import.
      return target.reachedAt
        ? `Checked daily. Not checked since ${formatInstant(target.reachedAt, zone, "short", { named: true })}: the source did not answer at ${at}.`
        : `Checked daily. Not checked yet: the source did not answer at ${at}.`;
  }
}

export function CredentialCheckLine({
  target,
  checksEnabled,
  testId,
}: {
  target: CredentialTargetView | null;
  checksEnabled: boolean;
  testId?: string;
}) {
  const zone = useDisplayTz();
  return (
    <SkyText size={200} color="muted" block testId={testId}>
      {checkSentence(target, checksEnabled, zone)}
    </SkyText>
  );
}

const SOURCE_WORDS = {
  token: "read from the token",
  provider: "read from the provider",
  entered: "entered by a person",
} as const;

/**
 * "Expires Mar 27, 2027 (read from the token)", or "Expired ..." once the date has passed. The
 * expiry is a calendar day (its UTC day), so it reads through `formatCalendarDay` like every other
 * date, never the browser's locale (B-41).
 */
export function expirySentence(expiry: CredentialExpiryView): string {
  const at = new Date(expiry.expiresAt);
  const day = Number.isNaN(at.getTime())
    ? expiry.expiresAt
    : formatCalendarDay(at.toISOString().slice(0, 10));
  const verb = expiry.daysLeft < 0 ? "Expired" : "Expires";
  return `${verb} ${day} (${SOURCE_WORDS[expiry.source]})`;
}
